import React, { useEffect } from 'react'
import { Button, Card, CardBody, CardTitle, Col, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import { updateAdminSettings } from 'helpers/API Calls/System & Settings/Admin Settings';

interface SMSFormValues {
  enableSMS: boolean;
  twilioSID: string;
  twilioAuthToken: string;
  twilioFrom: string;
}

function SMS({data}: any) {
  const formik = useFormik<SMSFormValues>({
    initialValues: {
      enableSMS: false,
      twilioSID: '',
      twilioAuthToken: '',
      twilioFrom: '',
    },    
    validationSchema: yup.object().shape({
      enableSMS: yup.boolean(),
      twilioSID: yup.string().when(['enableSMS'], {
        is: true,
        then: () => yup.string().required('Twilio SID is required')
      }),
      twilioAuthToken: yup.string().when(['enableSMS'], {
        is: true,
        then: () => yup.string().required('Twilio Auth Token is required')
      }),
      twilioFrom: yup.string().when(['enableSMS'], {
        is: true,
        then: () => yup.string().required('Twilio From Number is required')
      }),
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      setSubmitting(true);
      try{
        const reqData = [
        {
          key: "SMS_SERVICE",
          value: values.enableSMS ? 'ENABLED' : 'DISABLED',
          id: data.SMS_SERVICE.id,
        }, 
      ];
      
      if (values.enableSMS) {
        reqData.push(
          {
            key: "TWILIO_SID",
            value: values.twilioSID,
            id: data.TWILIO_SID.id,
          },
          {
            key: "TWILIO_AUTH_TOKEN",
            value: values.twilioAuthToken,
            id: data.TWILIO_AUTH_TOKEN.id,
          },
          {
            key: "TWILIO_FROM",
            value: values.twilioFrom,
            id: data.TWILIO_FROM.id,
          }
        );
      }
      
      const response = await updateAdminSettings({'settings' : reqData});
      if (response) {
        setSubmitting(false);
      }
      }
      catch (error) {
        console.error("Error updating settings:", error);
        setSubmitting(false);
      }
    }
  });  

  useEffect(() => {
    if (data) {
      formik.setValues({
        enableSMS: data?.SMS_SERVICE?.value === 'ENABLED',
        twilioSID: data?.TWILIO_SID?.value || '',
        twilioAuthToken: data?.TWILIO_AUTH_TOKEN?.value || '',
        twilioFrom: data?.TWILIO_FROM?.value || '',
      });
    }
  }, [data]);

  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);
      
  return (
    <Card>
      <CardBody>
        <CardTitle tag="h4" className='mb-4'>SMS Settings</CardTitle>
        <Form onSubmit={formik.handleSubmit} autoComplete="off">
          <Row>
            <Col md={12} className="mb-3">
              <div className="form-check form-switch">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="enableSMS"
                  name="enableSMS"
                  checked={formik.values.enableSMS}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" htmlFor="enableSMS">
                  Enable SMS Service
                </Label>
              </div>
            </Col>

            {formik.values.enableSMS && (
              <>
                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="twilioSID" className="form-label">Twilio SID</Label>
                    <Input
                      id="twilioSID"
                      name="twilioSID"
                      type="text"
                      className="form-control"
                      placeholder="Enter Twilio SID"
                      value={formik.values.twilioSID}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.twilioSID && formik.errors.twilioSID)}
                    />
                    {formik.touched.twilioSID && formik.errors.twilioSID && (
                      <FormFeedback>{String(formik.errors.twilioSID)}</FormFeedback>
                    )}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="twilioAuthToken" className="form-label">Twilio Auth Token</Label>
                    <Input
                      id="twilioAuthToken"
                      name="twilioAuthToken"
                      type="password"
                      className="form-control"
                      placeholder="Enter Twilio Auth Token"
                      value={formik.values.twilioAuthToken}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.twilioAuthToken && formik.errors.twilioAuthToken)}
                    />
                    {formik.touched.twilioAuthToken && formik.errors.twilioAuthToken && (
                      <FormFeedback>{String(formik.errors.twilioAuthToken)}</FormFeedback>
                    )}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="twilioFrom" className="form-label">Twilio From Number</Label>
                    <Input
                      id="twilioFrom"
                      name="twilioFrom"
                      type="text"
                      className="form-control"
                      placeholder="Enter Twilio From Number"
                      value={formik.values.twilioFrom}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.twilioFrom && formik.errors.twilioFrom)}
                    />
                    {formik.touched.twilioFrom && formik.errors.twilioFrom && (
                      <FormFeedback>{String(formik.errors.twilioFrom)}</FormFeedback>
                    )}
                  </div>
                </Col>
              </>
            )}

            <Col md={12}>
              <div className="text-end">
                <Button 
                  type="submit" 
                  color="primary" 
                  disabled={formik.isSubmitting || !formik.dirty}
                >
                  {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
}

export default SMS;
